import React, { ChangeEvent, forwardRef, memo, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { IErrMsg } from '../types'
import { DescriptionBtnWithPopup } from '../../buttons/PromptBtn/DescriptionBtnWithPopup'
import ErrMessageTooltip from '../../Errors/ErrMessageTooltip'
import { isValidDouble as isValidNumber } from '../DoubleInput/utils/isValidDouble'
import './NumInput.module.scss'
import { RequiredMark } from '../../ui/RequiredMark/RequiredMark'
import { RiskyParamUnlockBtn } from '../components/RiskyParam/RiskyParamUnlockBtn'

interface NumInputProps {
    name: string
    state: Record<string, any>
    setState: (state: Record<string, any>) => void
    touched?: Record<string, any>
    setTouched?: (state: Record<string, any>) => void
    errors?: Record<string, any>
    className?: string
    label?: string
    description?: string
    placeholder?: string
    changeWarning?: string
    isRequired?: boolean
    isDisabled?: boolean
    isColumn?: boolean
    isLabelHidden?: boolean
    isFeedbackFixed?: boolean
    errMsg?: IErrMsg
    onEnter?: Function
    onBlur?: Function
    onChange?: Function
}

export const NumInput = memo(
    forwardRef<HTMLInputElement, NumInputProps>(function NumInput(
        {
            name,
            state,
            setState,
            touched,
            setTouched,
            errors,
            className,
            label,
            description,
            placeholder,
            changeWarning,
            isRequired,
            isDisabled,
            isColumn,
            isLabelHidden,
            isFeedbackFixed,
            errMsg,
            onEnter,
            onBlur,
            onChange,
        },
        ref
    ) {
        const [isRiskyParam, setIsRiskyParam] = useState(!!changeWarning)
        const [showNumOnlyTooltip, setShowNumOnlyTooltip] = useState(false)
        const target = useRef(null)

        const isInvalid = touched && touched[name] && errors && errors[name]

        const handleUnlockRiskyParam = () => setIsRiskyParam(false)

        const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value

            if (isValidNumber(newValue)) {
                setShowNumOnlyTooltip(false)

                setTouched &&
                    touched &&
                    !touched[name] &&
                    setTouched((prevTouched: typeof touched) => ({
                        ...prevTouched,
                        [name]: true,
                    }))

                if (newValue === '') {
                    setShowNumOnlyTooltip(false)
                    setState({ ...state, [name]: null })
                    if (typeof onChange === 'function') onChange(null)
                    return
                }
                setState({ ...state, [name]: newValue })
                if (typeof onChange === 'function') onChange(newValue)
            } else {
                setShowNumOnlyTooltip(true)
            }
        }

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            setShowNumOnlyTooltip(false)
            if (typeof onBlur === 'function') return onBlur(e)

            setTouched &&
                setTouched((prevTouched: typeof touched) => {
                    return { ...prevTouched, [name]: true }
                })
            if (state[name] === '') {
                setState({ ...state, [name]: null })
            }
        }

        const handleOnKeyDown = (event: any) => {
            if (event.key === 'e' || event.key === ',') {
                event.preventDefault()
            }
            if (event.key === 'Enter' && typeof onEnter === 'function') {
                onEnter(event)
            }
        }

        return (
            <Form.Group
                style={{ maxWidth: '600px' }}
                className={`${className} ${
                    isInvalid && !isFeedbackFixed && 'mb-4'
                } d-flex flex-1 mt-1 mb-1 justify-content-between align-items-baseline`}
            >
                {label && !isLabelHidden ? (
                    <Form.Label
                        style={{
                            maxWidth: isColumn ? '30%' : 'auto',
                            opacity: isRiskyParam ? '50%' : '100%',
                        }}
                        className="label-main mt-1 text-capital pe-2"
                    >
                        <span className="label-main-wrapper p-relative">
                            {label}: {isRequired && <RequiredMark />}
                        </span>
                    </Form.Label>
                ) : null}
                <div
                    style={{ width: '70%' }}
                    className={`d-flex ${!isColumn ? 'flex-1' : ''}`}
                >
                    <span className="d-flex flex-column w-100 p-relative">
                        {isRiskyParam && <div className="input-overlay" />}
                        <Form.Control
                            className={`input-main ${
                                description && 'input-padding'
                            }`}
                            type="number"
                            placeholder={placeholder}
                            value={state[name] ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={handleOnKeyDown}
                            disabled={!!isDisabled}
                            isInvalid={isInvalid} // on true Form.Control.Feedback is shawn
                            name={name}
                            ref={ref ?? target}
                            tabIndex={isRiskyParam ? -1 : 0}
                        />
                        <Form.Control.Feedback
                            className="input-feedback"
                            type="invalid"
                        >
                            <FormattedMessage
                                id={errMsg?.message || 'field-validation-error'}
                                values={errMsg?.values}
                                tagName="span"
                            />
                        </Form.Control.Feedback>
                        {description && (
                            <DescriptionBtnWithPopup
                                type={'default'}
                                header={label ?? ''}
                                description={description}
                                className="mt-1 input-tooltip"
                            />
                        )}
                    </span>
                    {isRiskyParam && (
                        <RiskyParamUnlockBtn
                            onUnlockConfirm={handleUnlockRiskyParam}
                            changeWarning={changeWarning}
                        />
                    )}
                </div>
                <ErrMessageTooltip
                    message="num-only-toast"
                    error={showNumOnlyTooltip}
                    target={ref ?? target}
                />
            </Form.Group>
        )
    })
)
